import {
  AVAILABILITY_FILTER_ACCEPTING_LABEL,
  AVAILABILITY_FILTER_NOT_ACCEPTING_LABEL,
  RADIUS_OPTIONS,
  RATING_OPTIONS,
  SEARCH_CONFIG,
  SORT_BY_OPTIONS,
} from "../constants/providerSearch";
import { getAgeBracketLabel } from "../constants/ageBrackets";
import {
  getFundingLabel,
  resolveFundingCode,
} from "../constants/fundingTaxonomy";
import type { SearchFilters } from "../types/providerSearch";
import type { FilterChangeHandler } from "../types/filterTypes";
import { DEFAULT_SEARCH_FILTERS } from "./constants";
import { formatBusinessType } from "./providerFormatters";
import {
  mapServiceToDisplay,
  serviceDisplayKey,
} from "./serviceMappings";
import { mapServiceKeyToTaxonomy } from "./providerServiceCapabilities";

/**
 * Removable filter chips for the provider search form.
 * Keep field coverage aligned with countActiveSearchFilters in searchFilterUtils.ts
 * (count = chips.length + optional location).
 */
export type AppliedSearchFilterChip = {
  id: string;
  label: string;
  /** Full label for tooltips when the chip text is truncated. */
  title?: string;
  /** Strength chips only — default stays neutral (radius, type, payment, etc.). */
  tone?: "default" | "required" | "preferred";
  applyRemove: (onFilterChange: FilterChangeHandler) => void;
};

/**
 * Chip ids mirrored by always-visible primary controls in the expanded form /
 * mobile sheet. Omit these from the in-form Active filters rail (collapsed bar
 * still shows the full set).
 */
export const EXPANDED_FORM_OMITTED_CHIP_IDS = new Set([
  "business_type",
  "radius",
  "has_availability",
]);

/**
 * Payment fieldset chips — omit from the in-form rail when payment is elevated
 * to the primary band (case-linked search).
 */
export const EXPANDED_FORM_PAYMENT_CHIP_IDS = new Set([
  "accepts_medicaid",
  "accepts_medicaid_excluded",
  "accepts_medicare",
  "accepts_medicare_excluded",
  "accepts_private_insurance",
  "accepts_private_insurance_excluded",
]);

export type OmitExpandedFormPrimaryChipsOptions = {
  /** When payment lives in the primary row, drop payment chips too. */
  omitPayment?: boolean;
};

/** Drop primary-control chips while the expanded filter form is open. */
export function omitExpandedFormPrimaryChips(
  chips: AppliedSearchFilterChip[],
  options: OmitExpandedFormPrimaryChipsOptions = {}
): AppliedSearchFilterChip[] {
  const { omitPayment = false } = options;
  return chips.filter((chip) => {
    if (EXPANDED_FORM_OMITTED_CHIP_IDS.has(chip.id)) {
      return false;
    }
    if (omitPayment && EXPANDED_FORM_PAYMENT_CHIP_IDS.has(chip.id)) {
      return false;
    }
    return true;
  });
}

function optionLabel(
  options: readonly { value: string | number; label: string }[],
  value: string | number
): string {
  return (
    options.find((option) => String(option.value) === String(value))?.label ??
    String(value)
  );
}

function pushArrayItemChips(
  chips: AppliedSearchFilterChip[],
  filterKey: keyof SearchFilters,
  values: string[] | undefined,
  formatLabel: (item: string) => string,
  idPrefix: string,
  options?: {
    canonicalKey?: (item: string) => string;
    tone?: AppliedSearchFilterChip["tone"];
    titleForLabel?: (label: string) => string | undefined;
  }
): void {
  if (!values?.length) {
    return;
  }

  const seenKeys = new Set<string>();

  for (const item of values) {
    const canonicalKey = options?.canonicalKey?.(item) ?? item;
    if (seenKeys.has(canonicalKey)) {
      continue;
    }
    seenKeys.add(canonicalKey);

    const label = formatLabel(item);
    const matchingValues = values.filter((value) => {
      const valueKey = options?.canonicalKey?.(value) ?? value;
      return valueKey === canonicalKey;
    });
    const next = values.filter((value) => !matchingValues.includes(value));

    chips.push({
      id: `${idPrefix}-${canonicalKey}`,
      label,
      title: options?.titleForLabel?.(label),
      tone: options?.tone,
      applyRemove: (onFilterChange) => {
        onFilterChange(
          filterKey,
          (next.length > 0 ? next : undefined) as SearchFilters[typeof filterKey]
        );
      },
    });
  }
}

/** Human-readable removable chips for active search filters (excludes location). */
export function getActiveSearchFilterChips(
  filters: SearchFilters
): AppliedSearchFilterChip[] {
  const chips: AppliedSearchFilterChip[] = [];

  if (
    filters.radius_miles !== undefined &&
    filters.radius_miles !== SEARCH_CONFIG.DEFAULT_RADIUS_MILES
  ) {
    chips.push({
      id: "radius",
      label:
        optionLabel(RADIUS_OPTIONS, filters.radius_miles) ||
        `${filters.radius_miles} miles`,
      applyRemove: (onFilterChange) => {
        onFilterChange("radius_miles", SEARCH_CONFIG.DEFAULT_RADIUS_MILES);
      },
    });
  }

  if (filters.business_type) {
    chips.push({
      id: "business_type",
      label: formatBusinessType(filters.business_type),
      applyRemove: (onFilterChange) => {
        onFilterChange("business_type", undefined);
      },
    });
  }

  if (filters.min_rating) {
    chips.push({
      id: "min_rating",
      label: optionLabel(RATING_OPTIONS, filters.min_rating),
      applyRemove: (onFilterChange) => {
        onFilterChange("min_rating", undefined);
      },
    });
  }

  if (filters.has_availability !== undefined) {
    chips.push({
      id: "has_availability",
      label:
        filters.has_availability === true
          ? AVAILABILITY_FILTER_ACCEPTING_LABEL
          : AVAILABILITY_FILTER_NOT_ACCEPTING_LABEL,
      applyRemove: (onFilterChange) => {
        onFilterChange("has_availability", undefined);
      },
    });
  }

  pushArrayItemChips(
    chips,
    "age_groups_served",
    filters.age_groups_served,
    (value) => getAgeBracketLabel(value as "18+" | "55+"),
    "age"
  );

  if (filters.accepts_medicaid === true) {
    chips.push({
      id: "accepts_medicaid",
      label: "Medicaid",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_medicaid", undefined);
      },
    });
  } else if (filters.accepts_medicaid === false) {
    chips.push({
      id: "accepts_medicaid_excluded",
      label: "Medicaid: not required",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_medicaid", undefined);
      },
    });
  }

  if (filters.accepts_medicare === true) {
    chips.push({
      id: "accepts_medicare",
      label: "Medicare",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_medicare", undefined);
      },
    });
  } else if (filters.accepts_medicare === false) {
    chips.push({
      id: "accepts_medicare_excluded",
      label: "Medicare: not required",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_medicare", undefined);
      },
    });
  }

  if (filters.accepts_private_insurance === true) {
    chips.push({
      id: "accepts_private_insurance",
      label: "Private Insurance",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_private_insurance", undefined);
      },
    });
  } else if (filters.accepts_private_insurance === false) {
    chips.push({
      id: "accepts_private_insurance_excluded",
      label: "Private insurance: not required",
      applyRemove: (onFilterChange) => {
        onFilterChange("accepts_private_insurance", undefined);
      },
    });
  }

  pushArrayItemChips(
    chips,
    "disability_conditions",
    filters.disability_conditions,
    (value) => value,
    "disability",
    { tone: "required" }
  );

  pushArrayItemChips(
    chips,
    "accessibility_features",
    filters.accessibility_features,
    (value) => value,
    "accessibility",
    { tone: "required" }
  );

  pushArrayItemChips(
    chips,
    "funding_accepted",
    filters.funding_accepted,
    (value) => getFundingLabel(resolveFundingCode(value) ?? value, "search"),
    "funding"
  );

  pushArrayItemChips(
    chips,
    "required_core_services",
    filters.required_core_services,
    (value) => mapServiceToDisplay(value),
    "required_service",
    { canonicalKey: serviceDisplayKey, tone: "required" }
  );

  // Community service keys are always active (the former rollout flag was removed).
  pushArrayItemChips(
    chips,
    "required_service_keys",
    filters.required_service_keys,
    // Canonical community-service slugs → taxonomy labels (humanized fallback for
    // unknown keys; never a raw slug in the rail).
    (value) => mapServiceKeyToTaxonomy(value).label,
    "community_service",
    {
      tone: "required",
      titleForLabel: (label) => `Required: ${label}`,
    }
  );

  // Soft lane (product decision 2026-08-22): ranking boost only, dismissal is
  // undo-only. Same chip family as the required lane so users see the pairing.
  pushArrayItemChips(
    chips,
    "preferred_service_keys",
    filters.preferred_service_keys,
    (value) => mapServiceKeyToTaxonomy(value).label,
    "community_service_optional",
    {
      tone: "preferred",
      titleForLabel: (label) => `Preferred: ${label}`,
    }
  );

  pushArrayItemChips(
    chips,
    "nice_to_have_core_services",
    filters.nice_to_have_core_services,
    (value) => mapServiceToDisplay(value),
    "preferred_service",
    {
      canonicalKey: serviceDisplayKey,
      tone: "preferred",
      titleForLabel: (label) => `Preferred: ${label}`,
    }
  );

  pushArrayItemChips(
    chips,
    "languages",
    filters.languages,
    (value) => value,
    "language"
  );

  if (filters.other_capacity?.trim()) {
    const value = filters.other_capacity.trim();
    chips.push({
      id: "other_capacity",
      label: `Capacity: ${value}`,
      applyRemove: (onFilterChange) => {
        onFilterChange("other_capacity", undefined);
      },
    });
  }

  if (filters.verified_only === true) {
    chips.push({
      id: "verified_only",
      label: "Verified only",
      applyRemove: (onFilterChange) => {
        onFilterChange("verified_only", undefined);
      },
    });
  }

  if (filters.allow_cross_state === true) {
    chips.push({
      id: "allow_cross_state",
      label: "Include nearby states",
      applyRemove: (onFilterChange) => {
        onFilterChange("allow_cross_state", undefined);
      },
    });
  }

  if (
    filters.sort_by ||
    (filters.sort_order &&
      filters.sort_order !== SEARCH_CONFIG.DEFAULT_SORT_ORDER)
  ) {
    const sortLabel = filters.sort_by
      ? optionLabel(SORT_BY_OPTIONS, filters.sort_by)
      : "Custom";
    chips.push({
      id: "sort",
      label: filters.sort_order
        ? `Sort: ${sortLabel} (${filters.sort_order})`
        : `Sort: ${sortLabel}`,
      applyRemove: (onFilterChange) => {
        onFilterChange("sort_by", undefined);
        onFilterChange("sort_order", undefined);
      },
    });
  }

  return chips;
}

/** Reset chip-managed filters while preserving location and geo metadata. */
export function clearRemovableSearchFilters(filters: SearchFilters): SearchFilters {
  return {
    ...DEFAULT_SEARCH_FILTERS,
    location_query: filters.location_query,
    lat: filters.lat,
    lng: filters.lng,
    coordinates: filters.coordinates,
    location_type: filters.location_type,
    location_source: filters.location_source,
    address: filters.address,
  };
}

export { clearRestrictiveSearchFiltersForEmptyState } from "./providerSearchEmptyStateUtils";
